import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Autocomplete,
  Typography,
  Stack,
  Grid2,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { toast } from 'sonner';
import { Iconify } from '../Minimal/src/components/iconify';
import { TablePaginationCustom } from '../Minimal/src/components/pagination';
import { useTable } from '../Minimal/src/hooks/use-table';
import Header from '../Header';
import Footer from '../Footer';
import { useNavigate } from "react-router-dom";
import { API_URL, BASE_URL } from '../Config';
import config from "../common/Config";
import '../common/commonStyles.css';

const StockTakeList = () => {
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem("token");
  const [filterValue, setFilterValue] = useState('all');
  const [stockTakeList, setStockTakeList] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryValue, setCategoryValue] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [brandValue, setBrandValue] = useState(null);
  const [checkedValues, setCheckedValues] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [remarkValue, setRemarkValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [matchedCount, setMatchedCount] = useState(0);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const navigateToGRNList = useNavigate();

  const table = useTable({
    defaultRowsPerPage: 50,
    defaultDense: false,
    defaultOrder: 'desc',
    defaultOrderBy: 'name',
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const fetchCategoriesOptions = async () => {
    const apiCategoriesUrl = `${API_URL}/stock_take/getCategories`;
    try {
      const response = await fetch(apiCategoriesUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        }
      });

      const data = await response.json();
      const formattedOptions = data.data.category.map(item => ({
        label: item.name,
        value: item.id,
      }));
      setCategoryOptions(formattedOptions);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchBrandOptions = async () => {
    const apiBrandsUrl = `${API_URL}/stock_take/getBrands`;
    try {
      const response = await fetch(apiBrandsUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        }
      });

      const data = await response.json();
      const formattedOptions = data.data.brands.map(item => ({
        label: item.name,
        value: item.id,
      }));
      setBrandOptions(formattedOptions);
    } catch (error) {
      toast.error('Failed to load brands');
    }
  };

  const fetchStockTakeList = async () => {
    const params = new URLSearchParams();
    params.append('draw', 1);
    params.append('start', 0);
    params.append('length', 5000);
    params.append('categories', categoryValue ? categoryValue.value : '');
    params.append('brands', brandValue ? brandValue.value : '');
    params.append('filter', filterValue);

    const apiUrl = `${API_URL}/product/stockTakeList_v2?${params.toString()}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stock take list');
      }
      const data = await response.json();
      setStockTakeList(Array.isArray(data.data) ? data.data : []);
      setMatchedCount(data.matchedCount || 0);
      setUnmatchedCount(data.unmatchedCount || 0);
      setPendingCount(data.pendingCount || 0);
      setTotalCount(Number(data.totalCount) || 0);
      table.onChangePage(null, 0);
    } catch (error) {
      toast.error('Failed to load stock take list');
      setStockTakeList([]);
    }
  };

  const filteredList = useMemo(() => {
    let filtered = stockTakeList;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        const name = (item.name || '').toLowerCase();
        const bay = (item.bay || '').toString().toLowerCase();
        const rack = (item.rack || '').toString().toLowerCase();
        const remark = (item.remark || '').toLowerCase();
        return (
          name.includes(query) ||
          bay.includes(query) ||
          rack.includes(query) ||
          remark.includes(query)
        );
      });
    }

    if (filterValue === 'matched') {
      filtered = filtered.filter(item => Number(item.diff) === 0 && Number(item.piece_diff) === 0);
    } else if (filterValue === 'unmatched') {
      filtered = filtered.filter(item => Number(item.diff) !== 0 || Number(item.piece_diff) !== 0);
    } else if (filterValue === 'pending') {
      filtered = filtered.filter(item => !item.physical_qty && !item.physical_piece_qty);
    }

    return filtered;
  }, [stockTakeList, searchQuery, filterValue]);

  const searchFormData = () => {
    fetchStockTakeList();
  };

  const handleCheckboxChange = (id, checked) => {
    if (checked) {
      setCheckedValues(prev => [...prev, id]);
    } else {
      setCheckedValues(prev => prev.filter(val => val !== id));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const currentPageIds = filteredList
        .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
        .map(item => item.id.toString());
      setCheckedValues(prev => [...new Set([...prev, ...currentPageIds])]);
    } else {
      const currentPageIds = filteredList
        .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
        .map(item => item.id.toString());
      setCheckedValues(prev => prev.filter(val => !currentPageIds.includes(val)));
    }
  };

  const updatePhysicalQty = async (id, newValue) => {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('physical_qty', newValue);

    const apiPhysicalQtyUrl = `${API_URL}/stock_take/update_physical_qty`;
    try {
      const response = await fetch(apiPhysicalQtyUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        toast.error('Something went wrong, try again');
        return;
      }
      const data = await response.json();
      toast.success('Physical Qty updated successfully');
      fetchStockTakeList();
    } catch (error) {
      toast.error('Failed to update physical qty');
    }
  };

  const submitPdtRemark = async () => {
    if (checkedValues.length > 0) {
      const formData = new FormData();
      formData.append('remark', remarkValue);
      formData.append('product_id', checkedValues.join(','));

      const apiAddOrdersUrl = `${API_URL}/products/update_remark`;
      try {
        const response = await fetch(apiAddOrdersUrl, {
          method: 'POST',
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });

        if (!response.ok) {
          toast.error('Something went wrong, try again');
          return;
        }
        const data = await response.json();
        toast.success('Remark Added successfully');
        setShowModal(false);
        setRemarkValue('');
        setCheckedValues([]);
        fetchStockTakeList();
      } catch (error) {
        toast.error('Error adding remark. Please try again.');
      }
    } else {
      toast.error('Please select product to remark');
    }
  };

  const handelFormSubmit = async () => {
    if (stockTakeList.length === 0) {
      toast.error('No data to submit');
      return;
    }

    const formData = new FormData();
    formData.append('uniqueId', stockTakeList[0].uniqueid);

    const apiStockFormUrl = `${API_URL}/products/update_stock_take_product`;
    try {
      const response = await fetch(apiStockFormUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        toast.error('Something went wrong, try again');
        return;
      }

      const data = await response.json();
      toast.success('Data Submitted successfully');
      setTimeout(() => {
        navigateToGRNList("/stock_take/history");
      }, 1000);
    } catch (error) {
      toast.error('Error submitting data. Please try again.');
    }
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    fetchStockTakeList();
    fetchCategoriesOptions();
    fetchBrandOptions();

    document.title = 'Stock Take List';
    return () => {
      document.title = 'Ebusso';
    };
  }, []);

  useEffect(() => {
    table.onChangePage(null, 0);
  }, [searchQuery, filterValue]);

  const renderRow = (item, index) => {
    const {
      id,
      name,
      bay,
      rack,
      price,
      cost,
      quantity,
      physical_qty,
      diff,
      piece_qty,
      physical_piece_qty,
      piece_diff,
      remark,
    } = item;

    const isChecked = checkedValues.includes(id.toString());
    const rowDiff = Number(diff) || 0;
    const rowPieceDiff = Number(piece_diff) || 0;
    const isMatched = rowDiff === 0 && rowPieceDiff === 0;
    const isUnmatched = rowDiff !== 0 || rowPieceDiff !== 0;

    return (
      <TableRow
        key={`${id}-${index}-${name}`}
        className="table-cell-style"
        sx={{
          backgroundColor: isMatched ? "#d4edda" : isUnmatched ? "#f8d7da" : "transparent",
          "&:hover": {
            backgroundColor: isMatched ? "#c3e6cb" : isUnmatched ? "#f5c6cb" : "rgba(145, 158, 171, 0.04)",
          },
        }}
      >
        <TableCell sx={{ fontSize: "0.75rem", color: "#212b36", width: "50px", padding: "4px !important", overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <Checkbox
              checked={isChecked}
              onChange={(e) => handleCheckboxChange(id.toString(), e.target.checked)}
              size="medium"
              className="checkBox"
              sx={{
                padding: 0,
                margin: 0,
                "& .MuiSvgIcon-root": {
                  fontSize: "1.2rem",
                },
              }}
            />
          </Box>
        </TableCell>
        <TableCell className="table-cell-style" sx={{ width: "280px", minWidth: "280px" }}>{name || ''}</TableCell>
        <TableCell align="center" className="table-cell-style" sx={{ width: "80px", minWidth: "80px" }}>{bay || ''}</TableCell>
        <TableCell align="center" className="table-cell-style" sx={{ width: "70px", minWidth: "70px" }}>{rack || ''}</TableCell>
        <TableCell align="center" className="table-cell-style" sx={{ width: "70px", minWidth: "70px" }}>{price || ''}</TableCell>
        <TableCell align="center" className="table-cell-style" sx={{ width: "70px", minWidth: "70px" }}>{cost || ''}</TableCell>
        <TableCell align="center" className="table-cell-style" sx={{ width: "80px", minWidth: "80px" }}>{quantity || ''}</TableCell>
        <TableCell align="center" className="table-cell-style" sx={{ width: "80px", minWidth: "80px" }}>
          <TextField
            type="number"
            value={physical_qty || ''}
            onChange={(e) => {
              const newValue = parseInt(e.target.value) || 0;
              const updatedList = stockTakeList.map(item =>
                item.id === id ? { ...item, physical_qty: newValue } : item
              );
              setStockTakeList(updatedList);
            }}
            onBlur={(e) => {
              const newValue = parseInt(e.target.value) || 0;
              updatePhysicalQty(id, newValue);
            }}
            size="small"
            inputProps={{ min: 0, style: { textAlign: 'center' } }}
            sx={{
              width: "80px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                fontSize: "0.75rem",
                height: "32px",
                "& .MuiOutlinedInput-input": {
                  textAlign: "center",
                  py: 0.5,
                },
              },
            }}
          />
        </TableCell>
        <TableCell align="center" className="table-cell-style">{diff || ''}</TableCell>
        <TableCell align="center" className="table-cell-style">{piece_qty || ''}</TableCell>
        <TableCell align="center" className="table-cell-style">{physical_piece_qty || ''}</TableCell>
        <TableCell align="center" className="table-cell-style">{piece_diff || ''}</TableCell>
        <TableCell className="table-cell-style">
          <TextField
            value={remark || ''}
            disabled
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f5f7fa",
                fontSize: "0.75rem",
                height: "28px",
                "&.Mui-disabled": {
                  backgroundColor: "#f5f7fa",
                  color: "#212b36",
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.3,
                },
              },
            }}
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 2 }}>
                <Typography className="page-title">
                  Stock Take List
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  options={categoryOptions}
                  value={categoryValue}
                  onChange={(event, newValue) => {
                    setCategoryValue(newValue);
                  }}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className="input-border"
                      placeholder="Select Categories"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f5f7fa",
                          fontSize: "0.875rem",
                          "&:hover": {
                            backgroundColor: "#fff"
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#fff"
                          }
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      fontSize: "0.875rem",
                      "&:hover": {
                        backgroundColor: "#fff"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff"
                      }
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  options={brandOptions}
                  value={brandValue}
                  onChange={(event, newValue) => {
                    setBrandValue(newValue);
                  }}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className="input-border"
                      placeholder="Select Brands"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f5f7fa",
                          fontSize: "0.875rem",
                          "&:hover": {
                            backgroundColor: "#fff"
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#fff"
                          }
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      fontSize: "0.875rem",
                      "&:hover": {
                        backgroundColor: "#fff"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff"
                      }
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 4 }} display="flex" flexDirection={{xs:"column",md:"row"}} justifyContent="flex-end" alignItems="center" gap={2}>
                <FormControl 
                fullWidth 
                size="small"
                sx={{width: {xs: "100%", md: "200px" }}}
                >
                  <InputLabel sx={{ fontSize: "0.875rem" }}>Filter</InputLabel>
                  <Select
                    className="input-border"
                    value={filterValue}
                    label="Filter"
                    onChange={(e) => setFilterValue(e.target.value)}
                    sx={{
                      fontSize: "0.875rem",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f5f7fa",
                        fontSize: "0.875rem",
                        "&:hover": {
                          backgroundColor: "#fff"
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#fff"
                        }
                      },
                      "& .MuiSelect-select": {
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    <MenuItem value="all" sx={{ fontSize: "0.875rem" }}>All</MenuItem>
                    <MenuItem value="matched" sx={{ fontSize: "0.875rem" }}>Matched</MenuItem>
                    <MenuItem value="unmatched" sx={{ fontSize: "0.875rem" }}>Unmatched</MenuItem>
                    <MenuItem value="pending" sx={{ fontSize: "0.875rem" }}>Pending</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  className="button-variant-contained"
                  onClick={searchFormData}
                  sx={{
                    width: {xs: "100%", md: "auto"},

                  }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  className="button-variant-outlined"
                  onClick={() => setShowModal(true)}
                  sx={{
                    width: {xs: "100%", md: "auto"},

             
                  }}
                >
                  Bulk Remark
                </Button>

              </Grid2>
            </Grid2>


            <Box sx={{ my: 2 }} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={`Total: ${totalCount}`}
                  color="primary"
                  variant="soft"
                  size="medium"
                  sx={{ fontSize: "0.75rem", height: "24px" }}
                />
                <Chip
                  label={`Matched: ${matchedCount}`}
                  color="success"
                  variant="soft"
                  size="medium"
                  sx={{ fontSize: "0.75rem", height: "24px" }}
                />
                <Chip
                  label={`Unmatched: ${unmatchedCount}`}
                  color="error"
                  variant="soft"
                  size="medium"
                  sx={{ fontSize: "0.75rem", height: "24px" }}
                />
                <Chip
                  label={`Pending: ${pendingCount}`}
                  color="warning"
                  variant="soft"
                  size="medium"
                  sx={{ fontSize: "0.75rem", height: "24px" }}
                />
              </Stack>
              <TextField
                placeholder="Search by product name, bay, rack, or remark..."
                value={searchQuery}
                size="small"
                className="input-border"
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchQuery('');
                          }}
                          edge="end"
                        >
                          <Iconify icon="eva:close-fill" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  width: "100%",
                  maxWidth: "400px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f5f7fa",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#fff"
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff"
                    }
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Box>

            <TableContainer >
              <Table
                size="small"
                className="table-cell-border"
              >
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell sx={{ width: "50px", padding: "4px !important", overflow: "hidden" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                        <Checkbox
                          checked={filteredList
                            .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
                            .every(item => checkedValues.includes(item.id.toString()))}
                          indeterminate={
                            filteredList
                              .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
                              .some(item => checkedValues.includes(item.id.toString())) &&
                            !filteredList
                              .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
                              .every(item => checkedValues.includes(item.id.toString()))
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          size="medium"
                          className="checkBox"
                          sx={{
                            padding: 0,
                            margin: 0,
                            "& .MuiSvgIcon-root": {
                              fontSize: "1.2rem",
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: "280px", minWidth: "280px" }}>
                      Product Code - Name
                    </TableCell>
                    <TableCell align="center" sx={{ width: "80px", minWidth: "80px" }}>
                      Bay
                    </TableCell>
                    <TableCell align="center" sx={{ width: "70px", minWidth: "70px" }}>
                      Rack
                    </TableCell>
                    <TableCell align="center">
                      Sale P
                    </TableCell>
                    <TableCell align="center">
                      Cost P
                    </TableCell>
                    <TableCell align="center" sx={{ width: "80px", minWidth: "80px" }}>
                      Sys Qty
                    </TableCell>
                    <TableCell align="center" sx={{ width: "80px", minWidth: "80px" }}>
                      Phy Qty
                    </TableCell>
                    <TableCell align="center">
                      Diff
                    </TableCell>
                    <TableCell align="center" sx={{ width: "100px", minWidth: "100px" }}>
                      Piece Qty
                    </TableCell>
                    <TableCell align="center" sx={{ width: "100px", minWidth: "100px" }}>
                      Phy Piece
                    </TableCell>
                    <TableCell align="center">
                      Diff
                    </TableCell>
                    <TableCell>
                      Remark
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                        <Typography className="table-style">
                          {stockTakeList.length === 0
                            ? "No stock take data available."
                            : "No results found matching your search."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredList
                      .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
                      .map((item, index) => renderRow(item, index))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{  pt: 2 }}>
              <TablePaginationCustom
                className="pagination-styles"
                page={table.page}
                dense={table.dense}
                count={filteredList.length}
                rowsPerPage={table.rowsPerPage}
                onPageChange={table.onChangePage}
                onRowsPerPageChange={table.onChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                className="button-variant-contained"
                onClick={handelFormSubmit}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{mb:-2}}>Add Remark</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              className="input-border"
              label="Remark"
              value={remarkValue}
              onChange={(e) => setRemarkValue(e.target.value)}
              placeholder="Enter remark"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f5f7fa",
                  fontSize: "0.875rem",
                  "&:hover": {
                    backgroundColor: "#fff"
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#fff"
                  }
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)} variant="outlined" className="button-variant-outlined">
            Cancel
          </Button>
          <Button onClick={submitPdtRemark} variant="contained" className="button-variant-contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StockTakeList;


